import React from 'react';
// import { handleUIOptimisationTable } from '@shopee/chatbot-ui/lib/components/RichText';
import './style.scss';
import msgFailure from '@/assets/img/msg_failure.png';
import loadingCircle from '@/assets/img/loading_circle.png';
import classnames from 'classnames';
import { COUNTRY } from '@/utils/locale';
import { inject, observer } from 'mobx-react';
import { formatHtml } from '@/utils/formatHtml';
import { getChildInject, getHyperlinkType, isIntentLink, getIntentLink, isFormUrlLink, getFormUrlLink } from '@/utils/instantLoaded/tools';
import {
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
  REDIRECTION_BUTTON_TRIGGER_TYPE,
  GetMsgResCode,
} from '@/utils/constants';
import { dispatchTrack } from '@/utils/tracking';
import { ARTICLE_DETAIL_ANSWER_TYPE, TRACKING_PAGE } from '@/utils/tracking/constants';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';

const PREFIX = 'msg-container-wrapper';

const IdentifyType = {
  phone: 1,
  email: 2,
  url: 3,
};

const imgCssSelector = 'img:not(.ChatbotUI-accordion-item-icon)';

const IdentifyLink = {
  [IdentifyType.phone]: ({ content }) => (
    <a className="msg-link" href={`tel:${content}`}>
      {content}
    </a>
  ),
  [IdentifyType.email]: ({ content }) => (
    <a className="msg-link" href={`mailto:${content}`}>
      {content}
    </a>
  ),
  [IdentifyType.url]: ({ content }) => (
    <a
      className="msg-link"
      href="#"
      onClick={async () => {
        const { linkJump } = await import('@/utils/link');
        linkJump(content);
      }}
    >
      {content}
    </a>
  ),
};

class Msg extends React.PureComponent {
  static defaultProps = {
    // props
    content: '',
    from: '',
  };

  constructor(props) {
    super(props);

    this.domRef = React.createRef();

    this.isUIOptimisationTable = featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE);

    this.state = {
      findPhoneNumbersInText: null,
      metadata: null,
      richTextHtml: '',
    };
    this.isReSending = false;

    const { dialogueId, initStore, isHistory, historySessionId, historyDialogueId } = this.props;
    this.trackingArticleDetailBasicData = {
      chat_session_id: isHistory ? historySessionId : initStore.sessionId || '',
      chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
      intent_id: Number(props.kbIntentId),
      bot_id: Number(props.kbBotId),
      if_history: isHistory ? 1 : 0,
    };

    // [live-chat-split-change]
    this.isUnmount = false; // 标记组件是否unmount，如果unmount的话不再更新state，防止错误Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
  }

  componentDidMount() {
    const importMetaJsonPromise = import('@/assets/json/phonenumber.metadata.json');
    const importPhonenumberJsPromise = import('libphonenumber-js/core');

    Promise.all([importMetaJsonPromise, importPhonenumberJsPromise]).then(([metadataJson, libPhonenumberJs]) => {
      // [live-chat-split-change]
      if (this.isUnmount) {
        return;
      }
      this.setState({
        metadata: metadataJson.default,
        findPhoneNumbersInText: libPhonenumberJs.findPhoneNumbersInText,
      });
    });

    this.createHtml();

    this.handleAddEventListener();
  }

  componentWillUnmount() {
    this.isUnmount = true;
    this.handleRemoveEventListener();
  }

  replaceStringByMap = (content, replaceMap = []) => {
    replaceMap = replaceMap.filter((item) => item.start >= 0).sort((cur, next) => cur.start - next.start);

    if (!replaceMap.length || !content) {
      return content;
    }

    const { start, end, type } = replaceMap.shift();

    replaceMap.forEach((item) => {
      item.start -= end;
      item.end -= end;
    });

    const startStr = content.slice(0, start);
    const endStr = content.slice(end);
    const curStr = content.slice(start, end);
    const curIdentifyLink = IdentifyLink[type]?.({ content: curStr });

    return (
      <>
        {startStr}
        {curIdentifyLink}
        {this.replaceStringByMap(endStr, replaceMap)}
      </>
    );
  };

  handleBotMsg = (content) => {
    const actions = ['getPhoneReplaceMap', 'getEmailReplaceMap', 'getUrlReplaceMap'];

    const replaceMap = actions.map((action) => this[action]?.(content) || []).reduce((pre, cur) => pre.concat(cur), []);

    return this.replaceStringByMap(content, replaceMap);
  };

  getEmailReplaceMap = (content) =>
    this.getRegExpReplaceMap(content, {
      regExp: /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/gi,
      identifyType: IdentifyType.email,
    });

  getUrlReplaceMap = (content) =>
    this.getRegExpReplaceMap(content, {
      regExp: /(https?):\/\/([\w-]+\.)+[\w-]+(:\d+)?(\/[\w-./?%&#=]*)?/gi,
      identifyType: IdentifyType.url,
    });

  getPhoneReplaceMap = (content) => {
    const list = this.state.findPhoneNumbersInText(content, COUNTRY.toUpperCase(), this.state.metadata);

    const replaceMap = list.map((item) => {
      const { startsAt, endsAt } = item;

      return {
        start: startsAt,
        end: endsAt,
        type: IdentifyType.phone,
      };
    });

    return replaceMap;
  };

  getRegExpReplaceMap = (content, { regExp, identifyType }) => {
    const result = [];
    let arr;

    while ((arr = regExp.exec(content)) !== null) {
      result.push({
        start: arr.index,
        end: regExp.lastIndex,
        type: identifyType,
      });
    }

    return result;
  };

  changeUrlToLink = (content) => {
    const { from } = this.props;
    const reg = /(https?|ftp|file):\/\/([\w-]+\.)+[\w-]+(:\d+)?(\/[\w-./?%&=]*)?/;

    if (from === 'bot') {
      return this.state.findPhoneNumbersInText && this.state.metadata ? this.handleBotMsg(content) : content;
    } else if (from === 'agent' && reg.test(content)) {
      const match = content.match(reg);
      const url = match[0];
      const index = match.index;
      const urlLen = url.length;
      const startStr = content.slice(0, index);
      const endStr = content.slice(index + urlLen);
      return (
        <>
          {startStr}
          <a
            className="msg-link"
            href="#"
            onClick={async () => {
              // @MARK: lazyLoad: { linkJump }
              const { linkJump } = await import('@/utils/link');
              linkJump(url);
            }}
          >
            {url}
          </a>
          {this.changeUrlToLink(endStr)}
        </>
      );
    } else {
      return content;
    }
  };

  createHtml = async () => {
    const { content } = this.props;

    const sanitizeHtml = (await import('sanitize-html')).default;
    const sanitizeHtmlOptions = (await import('@/utils/sanitizeHtmlOptions')).default;
    // [live-chat-split-change]
    if (this.isUnmount) {
      return;
    }

    const formattedHtml = formatHtml(sanitizeHtml(content, sanitizeHtmlOptions));
    const { handleUIOptimisationTable } = await import('@shopee/chatbot-ui/lib/components/RichText/handleUIOptimisation');
    const resultHtml = this.isUIOptimisationTable ? handleUIOptimisationTable(formattedHtml, { isRichtext: true }) : formattedHtml;
    this.setState({ richTextHtml: resultHtml });
  };

  onClickEvent = async (e = {}) => {
    const href = e?.currentTarget?.getAttribute('href');
    const url = /^(in-app:)?(r?n)\/(\S+)/.test(href) ? href : e?.currentTarget?.href;
    const whitelist = ['tel:', 'mailto:'];
    const isUrlStartsWithWhitelist = whitelist.some((item) => url?.startsWith(item));

    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      target: TRACKING_TARGET.hyperlink,
      section: TRACKING_TARGET.richtextDetail,
      data: {
        ...this.trackingArticleDetailBasicData,
        content: e.currentTarget.textContent || '',
        hyperlink_type: getHyperlinkType(e?.currentTarget),
        answer_type: ARTICLE_DETAIL_ANSWER_TYPE.RICH_TEXT,
      },
    });


    if (isUrlStartsWithWhitelist) {
      if (url?.startsWith(whitelist[0])) {
        e?.preventDefault?.();
        window.location.href = url.split('?')[0];
      }
      return;
    }

    if (e.preventDefault) {
      e.preventDefault();
    } else {
      window.event.returnValue = false;
    }

    const { redirectionTo } = await import('@/utils/tools');
    if (isIntentLink(url)) {
      const { default: handleArticleRedirectionButtonClick } = await import('@/utils/intent/handleArticleRedirectionButtonClick');
      handleArticleRedirectionButtonClick({
        dialogueId: this.props.dialogueId,
        buttonName: e.target.innerText,
        intentId: getIntentLink(url),
        triggerType: REDIRECTION_BUTTON_TRIGGER_TYPE.INTENT,
      });
    } else if (isFormUrlLink(url)) {
      redirectionTo(getFormUrlLink(url));
    } else {
      url && redirectionTo(url);
    }
  };

  onImgClickEvent = async (e) => {
    const { openFullScreenImage } = await import('@/utils/tools');
    const currentEle = e.target;
    if (currentEle.src) {
      openFullScreenImage(currentEle.src);
    }
    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      target: TRACKING_TARGET.imageOrGif,
      section: TRACKING_TARGET.richtextDetail,
      page: TRACKING_PAGE.MAIN_PAGE,
      data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: currentEle.src || '' }] },
    });
  };


  handleRedirectionButtonClick = async (e) => {
    let data = {};
    try {
      data = JSON.parse(e.currentTarget?.dataset?.buttonJson);
    } catch { }

    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      section: TRACKING_TARGET.richtextDetail,
      target: TRACKING_TARGET[TRACKING_TYPE.ARTICLE_DETAIL_REDIRECTION_BUTTON],
      data: {
        ...this.trackingArticleDetailBasicData,
        content: data.buttonName,
      },
    });

    const { isHistory } = this.props;
    data.dialogueId = this.trackingArticleDetailBasicData.chat_dialogue_id;
    data.disableClick = isHistory;
    const { default: handleArticleRedirectionButtonClick } = await import('@/utils/intent/handleArticleRedirectionButtonClick');
    handleArticleRedirectionButtonClick(data);

  };

  handleAddEventListener() {
    setTimeout(() => {
      this.domRef?.current?.querySelectorAll('a')?.forEach((ele, index) => {
        ele.dataset.trackingname = TRACKING_TYPE.ARTICLE_DETAIL_HYPERLINK;
        ele.dataset.trackingindex = index;
        ele.dataset.trackingdata = JSON.stringify({
          ...this.trackingArticleDetailBasicData,
          content: ele.textContent || '',
          hyperlink_type: getHyperlinkType(ele),
          answer_type: ARTICLE_DETAIL_ANSWER_TYPE.RICH_TEXT,
        });

        addObserver(ele);

        ele.addEventListener('click', this.onClickEvent);

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.hyperlink,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: ele?.href || '' }] },
        });
      });

      this.domRef?.current?.querySelectorAll('iframe')?.forEach((ele) => {
        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.video,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: ele.src || '' }] },
        });
      });

      this.domRef?.current?.querySelectorAll(imgCssSelector)?.forEach((ele) => {
        ele.addEventListener('click', this.onImgClickEvent);

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.imageOrGif,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: ele?.src || '' }] },
        });
      });
      this.domRef?.current?.querySelectorAll('.redirection-button-to-taskbots-or-app-page')?.forEach((ele) => {
        addObserver(ele);
        ele.addEventListener('click', this.handleRedirectionButtonClick);

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.redirectionButton,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: ele?.buttonName || '' }] },
        });
      });

      this.domRef?.current?.querySelectorAll('table')?.forEach((ele) => {

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.table,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: ele.innerHTML || '' }] },
        });
      });

      this.domRef?.current?.querySelectorAll('accordion-block')?.forEach((ele) => {
        const tileElements = ele.querySelectorAll('.title');
        const textContents = Array.from(tileElements).map((element) => element.textContent);

        dispatchTrack({
          operation: TRACKING_OPERATION.IMPRESSION,
          target: TRACKING_TARGET.accordionCardTitle,
          section: TRACKING_TARGET.richtextDetail,
          page: TRACKING_PAGE.MAIN_PAGE,
          data: { viewed_objects: [{ ...this.trackingArticleDetailBasicData, content: textContents || '' }] },
        });
      });
    });
  }

  handleRemoveEventListener() {
    this.domRef?.current?.querySelectorAll('a')?.forEach(ele => {
      ele.removeEventListener('click', this.onClickEvent);
    });
    this.domRef?.current?.querySelectorAll(imgCssSelector)?.forEach(ele => {
      ele.removeEventListener('click', this.onImgClickEvent);
    });
    this.domRef?.current?.querySelectorAll('.redirection-button-to-taskbots-or-app-page')?.forEach(ele => {
      removeObserver(ele);
      ele.removeEventListener('click', this.handleRedirectionButtonClick);
    });
  }

  onResendMessage = async () => {
    const reporter = await import('@/utils/report');
    try {
      const { content, agentStore, curPos } = this.props;
      const { agentData = {}, sessionID } = agentStore;
      const curSessionAgentData = agentData[sessionID];
      const resendMsgFail = 'resend message fail';
      const baseErrorReportParams = { level: 'error', sessionId: sessionID };
      let isError = false;
      if (this.isReSending || typeof content !== 'string' || !curSessionAgentData || typeof curPos !== 'number') {
        // 排除重复重发的错误log上报
        if (!this.isReSending) {
          reporter?.default?.apiLog?.(resendMsgFail, { ...baseErrorReportParams, error: JSON.stringify({ content, curSessionAgentData: Boolean(curSessionAgentData), curPos }) });
        }
        return;
      }
      // 防止多次点击反复重新发送
      this.isReSending = true;
      const { postChatMsg, reviewChatEndText } = await import('@/utils/agent');
      // 如果会话已结束或暂时中断
      if (agentStore.agentEndedReason || agentStore.isDisconnected) {
        // 回显agent离开文案
        reviewChatEndText();
        isError = true;
        reporter?.default?.apiLog?.(resendMsgFail, { ...baseErrorReportParams, error: `session has ${agentStore.agentEndedReason ? 'ended' : 'disconnected'}` });
      } else {
        // 用户主动重发数
        reporter?.default?.apiBatchReport?.({ type: 'count', value: { label: 'channel_chat_message_user_resend_total' }});
        const { code: resCode } = await postChatMsg({
          text: content,
          type: 'TEXT',
        });
        isError = Boolean(resCode !== GetMsgResCode.SUCCESS);
        if (isError) {
          reporter?.default?.apiLog?.(resendMsgFail, { ...baseErrorReportParams, error: `postChatMsg error with code ${resCode}` });
        }
      }
      this.isReSending = false;
      agentStore.UPDATE_AGENT_DATA_ITEM({
        loading: false,
        requesting: false,
        error: isError,
      }, curPos);
    } catch (error) {
      reporter?.default?.apiLog?.('resend message fail', { level: 'error', error: JSON.stringify(error) });
    }
  };

  render() {
    const { content, loading, error, isKbRichText, className = '' } = this.props;
    const contentList = content.split('<br>');

    if (isKbRichText) {
      return (
        <div
          className={classnames(
            PREFIX,
            {
              'ui-optimisation-button-container': featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON),
              'ui-optimisation-table-container': this.isUIOptimisationTable,
            },
            className,
          )}
        >
          <div ref={this.domRef} className={classnames('content', { 'th-strong': COUNTRY === 'th' })} dangerouslySetInnerHTML={{ __html: this.state.richTextHtml }}></div>
        </div>
      );
    }
    return (
      <>
        {content ? (
          <div className={classnames(PREFIX, className)}>
            <div>
              {contentList.map((content, index) => {
                if (index) {
                  return (
                    <React.Fragment key={`content-${index}`}>
                      <br />
                      {this.changeUrlToLink(content)}
                    </React.Fragment>
                  );
                }
                return <React.Fragment key={`content-${index}`}>{this.changeUrlToLink(content)}</React.Fragment>;
              })}
            </div>
            {loading ? (
              <div className="status-icon loading">
                <img src={loadingCircle} />
              </div>
            ) : (
              error && (
                <div className="status-icon" onClick={() => this.onResendMessage()}>
                  <img src={msgFailure} />
                </div>
              )
            )}
          </div>
        ) : null}
      </>
    );
  }
}

export default inject(getChildInject('agentStore', 'initStore'))(observer(Msg));
// 如果以加了inject和observer包裹组件，jest测试那边会报：ShallowWrapper::setState() can only be called on class components的错误，这边再导出Msg组件
export { Msg };
